import { useReducer } from 'react';
import { useSearchParams } from 'react-router';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

interface State {
    page: number;
    size: number;
    searchString?: string;
    sort?: string;
}

type ActionTypes = 'changePageModel' | 'changeSort' | 'changeSearchString';

interface Action {
    type: ActionTypes;
    payload: string | undefined | GridPaginationModel;
}

const defaultState: State = {
    page: 0,
    size: 25,
    sort: undefined,
    searchString: undefined,
};

function reducer(state: State, action: Action) {
    switch (action.type) {
        case 'changePageModel': {
            const { page, pageSize } = action.payload as GridPaginationModel;
            return {
                ...state,
                page: page,
                size: pageSize,
            };
        }
        case 'changeSort':
            return {
                ...state,
                page: 0,
                sort: action.payload as string | undefined,
            };
        case 'changeSearchString':
            return {
                ...state,
                page: 0,
                searchString: action.payload as string | undefined,
            };
        default:
            throw new Error();
    }
}

export const usePaging = (initialState: State = defaultState) => {
    const [searchParams, setSearchParams] = useSearchParams({
        page: initialState.page.toString(),
        size: initialState.size.toString(),
    });

    // Generated by Copilot
    const urlParams = Array.from(searchParams.entries()).reduce(
        (acc, [key, value]) => {
            if (key === 'page' || key === 'size') {
                return {
                    ...acc,
                    [key]: parseInt(value, 10),
                };
            } else {
                return acc;
            }
        },
        {
            page: initialState.page,
            size: initialState.size,
        } as Record<'page' | 'size', number>
    );

    const [state, dispatch] = useReducer(reducer, urlParams);

    const updatePageModel = (payload: GridPaginationModel) => {
        setSearchParams(prev => ({
            size: payload.pageSize.toString(),
            page: payload.page.toString(),
        }));
        dispatch({ type: 'changePageModel', payload });
    };

    const updateSort = ([sortModel]: GridSortModel) => {
        const payload = sortModel
            ? `${sortModel.field},${sortModel.sort}`
            : undefined;

        // setSearchParams(prev => ({
        //     // ...prev,
        //     sort: payload ?? '',
        // }));
        dispatch({ type: 'changeSort', payload });
    };

    const updateSearchString = (payload?: string) => {
        dispatch({ type: 'changeSearchString', payload });
    };

    const hasNextPage = (total: number | undefined) => {
        if (!total) {
            return false;
        }

        return total / (state.page * state.size) > 1;
    };

    return {
        ...(state as State),
        updatePageModel,
        updateSort,
        updateSearchString,
        hasNextPage,
    };
};
